'use client';

import React, { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { getUi, hideModal } from '@/store/reducers/ui-slice';
import { PaginatedResponse, Permission } from '@alien-platform/bo-sdk-v2';
import { MethodResponse } from '@alien-platform/cms-bo';
import { Alert, Button, Grid } from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';

function ApplyPermissionGroupModalContent(): React.JSX.Element {
  const { modal } = useAppSelector(getUi);
  const { user } = useSelector((state: RootState) => state.user);
  const searchParams = useSearchParams();
  const selectedOpId = searchParams.get('opId') || undefined;
  const content = modal?.content as { userId: string; permissions: Permission[] };
  const [groupOptions, setGroupOptions] = useState<Permission[]>([]);
  const router = useRouter();
  const pathname = usePathname();

  const [dto, setDto] = useState({
    id: content.userId,
    opId: user?.opId,
    permissionGroupId: '',
    permissions: content.permissions,
  });

  const [groupOpId, setGroupOpId] = useState<string | null>(null);

  // useEffect(() => {
  //   if (dto.permissionGroupId && dto.permissionGroupId !== '') {
  //     const targetOpId = groupOptions.find((item) => item.id === dto.permissionGroupId)?.opId;
  //     if (targetOpId) {
  //       setGroupOpId(targetOpId);
  //     }
  //   }
  // }, [dto.permissionGroupId]);

  const generalInputs: CustomConfigInputProps[] = [
    {
      label: 'Choose Permission Group',
      name: 'permissionGroupId',
      value: dto.permissionGroupId,
      type: InputType.SELECT,
      menuItems: groupOptions?.map((item) => ({ label: item.name, value: item.id })),
    },
  ];

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = (): void => {
    dispatch(hideModal());
  };

  const handleApply = (): void => {
    const updatedSearchParams = new URLSearchParams(Array.from(searchParams.entries()));

    updatedSearchParams.set('group', dto.permissionGroupId);
    if (groupOpId) {
      updatedSearchParams.set('groupOpId', groupOpId);
    }
    const query = updatedSearchParams.toString();
    router.push(`${pathname}?${query}`);
    dispatch(hideModal());
  };

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await fetch(`/api/permissions/permissions-groups?opId=${selectedOpId}`, {});
      const result: MethodResponse<PaginatedResponse<Permission>> = await response.json();
      if (response.ok && result.success && result?.data?.items) {
        setGroupOptions(result.data.items);
      }
    } catch (e) {
      toast.error('Error: 500 | Unable to get permissions groups');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  return (
    <Stack spacing={3}>
      <Grid container spacing={4}>
        {generalInputs.map((item, index) => (
          <CustomConfigInputV2 key={index} defaultData={dto} setDefaultData={setDto} {...item} />
        ))}
      </Grid>
      <Alert severity="info">You can edit applied permission group before saving it.</Alert>
      <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
        <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
          Close
        </Button>

        <Button loading={isLoading} onClick={handleApply} color="success" sx={{ flex: '1 1 auto' }} variant="contained">
          Apply
        </Button>
      </Stack>
    </Stack>
  );
}

export default ApplyPermissionGroupModalContent;
